/**
 * Hook centralisé pour la validation / rejet des demandes de congés.
 *
 * Contexte produit : depuis avril 2026, la validation des congés passe
 * exclusivement par le secrétariat (et plus par la direction/JLO).
 * La direction reçoit désormais des notifications pour visibilité.
 *
 * Garanties apportées :
 *   - Audit DB : `validated_by`, `validated_at`, `rejected_by`, `rejected_at`
 *     mis à jour automatiquement
 *   - Audit trail dans `payroll_corrections` (action: 'validate'/'reject')
 *   - Notification employé (sheet_validated/sheet_rejected recyclé pour congés)
 *   - Notification direction (tous les admins) : synthèse de l'action secrétaire
 *   - Toast feedback utilisateur
 */
import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from './useToast';

type LeaveStatus = 'pending' | 'validated' | 'rejected';

interface LeaveContext {
  id: string;
  employee_id: string;
  status: LeaveStatus;
  type: string;
  start_date: string;
  end_date: string;
  employee?: { full_name: string } | null;
}

export function useLeaveActions() {
  const { user } = useAuth();
  const { toast } = useToast();

  /** Récupère le contexte minimal d'un congé + le nom de l'employé pour notif */
  const getLeaveContext = useCallback(async (leaveId: string): Promise<LeaveContext | null> => {
    const { data, error } = await supabase
      .from('leaves')
      .select('id, employee_id, status, type, start_date, end_date, employee:employees!employee_id(full_name)')
      .eq('id', leaveId)
      .single();
    if (error || !data) return null;
    return data as unknown as LeaveContext;
  }, []);

  /** Notification simple à un destinataire */
  const notify = useCallback(async (
    recipientId: string,
    type: 'leave_validated' | 'leave_rejected',
    title: string,
    message: string,
    relatedEmployeeId?: string,
  ) => {
    await supabase.from('notifications').insert({
      recipient_id: recipientId,
      type,
      title,
      message,
      related_employee_id: relatedEmployeeId ?? null,
    });
  }, []);

  /** Notifie TOUS les admins (direction) — synthèse des actions secrétariat */
  const notifyAllAdmins = useCallback(async (
    type: 'leave_validated' | 'leave_rejected',
    title: string,
    message: string,
    employeeId: string,
  ) => {
    const { data: admins } = await supabase
      .from('employees')
      .select('id')
      .eq('role', 'admin')
      .eq('is_active', true);
    if (!admins || admins.length === 0) return;
    await Promise.all(admins.map(a => notify(a.id, type, title, message, employeeId)));
  }, [notify]);

  /** Audit dans payroll_corrections (réutilisé pour congés via leave_id) */
  const insertAudit = useCallback(async (
    leave: LeaveContext,
    action: 'validate' | 'reject',
    reason: string | null,
  ) => {
    if (!user) return;
    await supabase.from('payroll_corrections').insert({
      time_entry_id: null,
      weekly_sheet_id: null,
      leave_id: leave.id,
      corrected_by: user.id,
      actor_role: user.role,
      actor_page: 'secretariat_leaves',
      action,
      record_type: 'leave',
      field_name: 'status',
      old_value: leave.status,
      new_value: action === 'validate' ? 'validated' : 'rejected',
      reason,
    });
  }, [user]);

  /** Formate une période de congé pour les notifications : "du 15/04 au 20/04" */
  const formatPeriod = useCallback((startDate: string, endDate: string) => {
    const fmt = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    return startDate === endDate ? `le ${fmt(startDate)}` : `du ${fmt(startDate)} au ${fmt(endDate)}`;
  }, []);

  // ==========================================================================
  // validateLeave
  // ==========================================================================
  const validateLeave = useCallback(async (leaveId: string): Promise<boolean> => {
    if (!user) return false;

    const leave = await getLeaveContext(leaveId);
    if (!leave) {
      toast.error('Demande de congé introuvable.');
      return false;
    }
    if (leave.status !== 'pending') {
      toast.warning('Cette demande a déjà été traitée.');
      return false;
    }

    const { data: updated, error } = await supabase
      .from('leaves')
      .update({
        status: 'validated',
        validated_by: user.id,
        validated_at: new Date().toISOString(),
        rejection_reason: null,
        rejected_at: null,
        rejected_by: null,
      })
      .eq('id', leaveId)
      .select('id');

    if (error) {
      console.error('[useLeaveActions] validateLeave error:', error);
      toast.error(`Erreur lors de la validation : ${error.message}`);
      return false;
    }
    if (!updated || updated.length === 0) {
      toast.error('Validation refusée par la sécurité (RLS).');
      return false;
    }

    await insertAudit(leave, 'validate', null);

    const empName = leave.employee?.full_name ?? 'Un employé';
    const period = formatPeriod(leave.start_date, leave.end_date);

    // Notification à l'employé
    await notify(
      leave.employee_id,
      'leave_validated',
      'Congé validé',
      `Votre demande de congé ${period} a été validée.`,
    );

    // Notification synthèse à la direction
    await notifyAllAdmins(
      'leave_validated',
      'Congé validé (synthèse direction)',
      `${empName} — congé ${period} validé par le secrétariat.`,
      leave.employee_id,
    );

    toast.success('Demande validée.');
    return true;
  }, [user, toast, getLeaveContext, insertAudit, notify, notifyAllAdmins, formatPeriod]);

  // ==========================================================================
  // rejectLeave
  // ==========================================================================
  const rejectLeave = useCallback(async (
    leaveId: string,
    reason: string,
  ): Promise<boolean> => {
    if (!user) return false;
    if (!reason.trim()) {
      toast.warning('Un motif de rejet est requis.');
      return false;
    }

    const leave = await getLeaveContext(leaveId);
    if (!leave) {
      toast.error('Demande de congé introuvable.');
      return false;
    }
    if (leave.status !== 'pending') {
      toast.warning('Cette demande a déjà été traitée.');
      return false;
    }

    const { data: updated, error } = await supabase
      .from('leaves')
      .update({
        status: 'rejected',
        rejection_reason: reason,
        rejected_at: new Date().toISOString(),
        rejected_by: user.id,
        validated_at: null,
        validated_by: null,
      })
      .eq('id', leaveId)
      .select('id');

    if (error) {
      console.error('[useLeaveActions] rejectLeave error:', error);
      toast.error(`Erreur lors du rejet : ${error.message}`);
      return false;
    }
    if (!updated || updated.length === 0) {
      toast.error('Rejet refusé par la sécurité (RLS).');
      return false;
    }

    await insertAudit(leave, 'reject', reason);

    const empName = leave.employee?.full_name ?? 'Un employé';
    const period = formatPeriod(leave.start_date, leave.end_date);

    // Notification à l'employé
    await notify(
      leave.employee_id,
      'leave_rejected',
      'Congé refusé',
      `Votre demande de congé ${period} a été refusée. Motif : ${reason}`,
    );

    // Notification synthèse à la direction
    await notifyAllAdmins(
      'leave_rejected',
      'Congé refusé (synthèse direction)',
      `${empName} — congé ${period} refusé par le secrétariat. Motif : ${reason}`,
      leave.employee_id,
    );

    toast.success('Demande rejetée. L\'employé a été notifié.');
    return true;
  }, [user, toast, getLeaveContext, insertAudit, notify, notifyAllAdmins, formatPeriod]);

  return { validateLeave, rejectLeave };
}
